import * as React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { LOGO_SRC, LOGO_SRCSET } from "./Logo";
import { IMG, IMG_SET } from "./Images";
import "./styles.css";

/* ============================================================================
   Talkblox — Single Page Landing
   ----------------------------------------------------------------------------
   ลำดับ Section ตามข้อกำหนด §25
     Header → Hero → Industry → Why Talkblox → Product Capability
     → Impact & Traction → Partner Ecosystem → Conversion CTA → Footer

   หมายเหตุสำหรับทีมหลังบ้าน
   1. รูปทั้งหมดใช้ <Media /> เป็น placeholder — ส่ง prop `src` + `alt`
      เมื่อมีไฟล์จริง จะ render <img loading="lazy"> ให้อัตโนมัติ
      ทุกจุดล็อก aspect-ratio ไว้แล้วเพื่อกัน Layout Shift (§22)
   2. ข้อความทั้งหมดอยู่ใน `copy.th` / `copy.en` ย้ายไป i18n หรือ CMS ได้ทั้งก้อน
      โครงสร้างสองภาษาเหมือนกันทุก key จึงตรวจความครบถ้วนได้ด้วย type
   3. เมื่อทำ routing จริง (/th/, /en/) ให้อ่านภาษาจาก URL แล้วส่งเป็น
      initial value ของ <App defaultLang="..." /> แทนการใช้ state ภายใน
   ========================================================================== */

/* --------------------------------------------------------------- i18n copy -- */

type Lang = "th" | "en";

/* ── การเคลื่อนไหวกับ prefers-reduced-motion ────────────────────────────────
   แยกเป็นสองประเภท เพราะ WCAG มองมันคนละแบบ

   1) เข้าฉากครั้งเดียว — typing, นับเลข, วาดกราฟ, แท่งโต
      จบภายใน 1–4 วินาที แล้วหยุดถาวร WCAG 2.2.2 คุมเฉพาะสิ่งที่เคลื่อนไหว
      เกิน 5 วินาทีหรือวนไม่จบ อันนี้จึงไม่เข้าข่าย ปล่อยให้เล่นได้

   2) วนไม่จบ — ออร่าเสียง, วงคลื่นไมค์, การ์ดลอย, เคอร์เซอร์กะพริบ
      อันนี้คือสิ่งที่รบกวนผู้ใช้จริง ต้องปิดตามค่าระบบเสมอ

   ถ้าอยากให้กลุ่มแรกเคารพค่าระบบด้วย เปลี่ยนบรรทัดล่างเป็น true            */
const RESPECT_REDUCED_MOTION_ENTRANCE = false;

/* กลุ่มที่ 2 — ตั้ง false ไว้ชั่วคราวเพื่อให้เห็นจังหวะกระพริบระหว่างพัฒนา
   ก่อนขึ้น production ต้องเปลี่ยนกลับเป็น true เพราะการกระพริบวนไม่จบ
   คือสิ่งที่ WCAG 2.2.2 คุมไว้โดยตรง และรบกวนผู้ใช้บางกลุ่มจริง            */
const RESPECT_REDUCED_MOTION_AMBIENT = false;

function prefersReducedMotion() {
  return (
    RESPECT_REDUCED_MOTION_ENTRANCE &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const copy = {
  th: {
    langLabel: "TH",
    langSwitchTo: "เปลี่ยนเป็นภาษาอังกฤษ",
    skip: "ข้ามไปยังเนื้อหาหลัก",
    menuOpen: "เปิดเมนู",
    menuClose: "ปิดเมนู",
    home: "Talkblox หน้าแรก",
    // ยุบจาก 7 เหลือ 5 — "อุตสาหกรรม" กับ "สำหรับองค์กร" เป็นเรื่องเดียวกัน
    // ในมุมลูกค้า จึงรวมเป็น "โซลูชัน" ส่วน "ติดต่อเรา" ไปอยู่ที่ปุ่ม CTA แทน
    nav: [
      { label: "หน้าแรก", href: "#top" },
      { label: "ผลิตภัณฑ์", href: "#product" },
      { label: "โซลูชัน", href: "#industry" },
      { label: "พันธมิตร", href: "#partner" },
      { label: "เกี่ยวกับเรา", href: "#why" },
    ],
    ctaShort: "ทดลองใช้งาน",
    hero: {
      eyebrow: "Real skills. Real people. Brighter opportunities.",
      headline: ["ฝึกทักษะการสื่อสารเฉพาะอาชีพ", "เฉพาะคุณ"],
      support: "ด้วยเทคโนโลยีปัญญาประดิษฐ์แบบมัลติโมดัล",
      english: "Multimodal AI Driven Occupational Communication Skills Platform",
      benefits: [
        { icon: "target", line1: "บทเรียน", line2: "เฉพาะอาชีพ" },
        { icon: "conversation", line1: "ฝึกจาก", line2: "สถานการณ์จริง" },
        { icon: "analytics", line1: "วัดผลได้", line2: "เห็นพัฒนาการ" },
      ],
      primaryCta: "ทดลองใช้งาน",
      secondaryCta: "ชมวิดีโอแนะนำ",
      script: "Skills\nCreate\nOpportunities",
      portrait: "ภาพบุคลากรวัยทำงาน มองไปข้างหน้า พื้นหลังเมือง",
      people: ["พนักงานต้อนรับ", "วิศวกรหน้างาน", "บุคลากรการแพทย์"],
      appAvatar: "ตัวอย่างคู่สนทนาในบทเรียน",
      floatCard: ["ฝึกได้ทุกที่", "ทุกเวลา"],
      phone: ["Learn", "Practice", "Perform"],
      tips: [
        "ออกเสียง /θ/ ชัดขึ้น 15%",
        "ตอบได้เร็วขึ้น 1.2 วินาที",
        "ใช้คำสุภาพเพิ่มขึ้น 3 ครั้ง",
      ],
      radar: {
        title: "ความพร้อมในการสื่อสาร",
        unit: "คะแนน WTC",
        axes: [
          { label: "มั่นใจ", value: 82 },
          { label: "คล่อง", value: 68 },
          { label: "ออกเสียง", value: 74 },
          { label: "คำศัพท์", value: 79 },
          { label: "โต้ตอบ", value: 71 },
        ],
      },
      levels: {
        title: "ระดับทักษะรายสัปดาห์",
        score: "71",
        unit: "จาก 100",
        bars: [42, 55, 63, 71, 84],
      },
    },
    industry: {
      heading: ["เรียนจากบริบทจริง", "เฉพาะงานของคุณ"],
      lead: "เนื้อหาเฉพาะอาชีพ ปรับตามผู้เรียนและบริบทขององค์กร พร้อมฝึกได้ทุกที่ทุกเวลา",
      script: "ทุกอาชีพ\nมีโอกาสที่มากกว่า",
      scriptSub: "เมื่อสื่อสารได้อย่างมั่นใจ",
      label: "กลุ่มอุตสาหกรรมที่รองรับ",
      items: [
        { icon: "hospitality", th: ["การท่องเที่ยว", "และโรงแรม"], en: "Hospitality" },
        { icon: "logistics", th: ["โลจิสติกส์", "และการค้า"], en: "Logistics" },
        { icon: "healthcare", th: ["การแพทย์", "และสุขภาพ"], en: "Healthcare" },
        { icon: "business", th: ["ธุรกิจบริการ", "และองค์กรระหว่างประเทศ"], en: "Business" },
      ],
      mediaPrefix: "บริบทการทำงานสาย",
      cta: "ระบุอุตสาหกรรมของคุณ",
    },
    lead: {
      open: "ระบุอุตสาหกรรมของคุณ",
      close: "ปิดหน้าต่าง",
      title: "ออกแบบการฝึกให้ตรงกับธุรกิจของคุณ",
      sub: "ตอบไม่กี่ข้อ ทีมงานจะเตรียมตัวอย่างการใช้งานที่ตรงกับบริบทของคุณกลับไป",
      stepLabel: "ขั้นที่",
      of: "จาก",
      back: "ย้อนกลับ",
      next: "ถัดไป",
      submit: "ส่งข้อมูล",
      sending: "กำลังส่ง...",
      required: "จำเป็น",
      steps: [
        {
          title: "กรุณาเลือกชนิดธุรกิจของคุณ เพื่อให้เราเข้าใจคุณมากขึ้น",
          hint: "เลือกบทบาทที่ตรงที่สุด",
        },
        {
          title: "ธุรกิจของคุณอยู่ในบริบทใด",
          hint: "เลือกอุตสาหกรรมได้มากกว่าหนึ่ง",
        },
        {
          title: "ให้ทีมงานติดต่อกลับได้อย่างไร",
          hint: "เราใช้ข้อมูลนี้เพื่อติดต่อเรื่อง Talkblox เท่านั้น",
        },
      ],
      roles: [
        { value: "หน่วยงานภาครัฐ", en: "Public Sector", icon: "hospitality" },
        { value: "หน่วยงานภาคเอกชน", en: "Private Sector", icon: "business" },
        { value: "ตัวแทนจำหน่าย", en: "Reseller", icon: "partner" },
        { value: "ผู้ให้บริการฝึกอบรม", en: "Training Provider", icon: "training" },
        { value: "ผู้พัฒนาเนื้อหา", en: "Content / Course Provider", icon: "course" },
        { value: "สถาบันการศึกษา", en: "Education Partner", icon: "education" },
      ],
      industryLabel: "อุตสาหกรรม",
      industries: [
        "ท่องเที่ยวและโรงแรม",
        "โลจิสติกส์และการค้า",
        "การแพทย์และสุขภาพ",
        "ธุรกิจบริการ",
        "องค์กรระหว่างประเทศ",
        "การผลิต",
        "ค้าปลีก",
        "การเงินและประกัน",
        "เทคโนโลยี",
        "การศึกษา",
        "ราชการ",
        "อื่น ๆ",
      ],
      businessLabel: "ธุรกิจหลักคืออะไร",
      businessPlaceholder: "เช่น โรงแรม 4 ดาว 2 สาขา เน้นลูกค้าต่างชาติ",
      sizeLabel: "จำนวนผู้ใช้งานโดยประมาณ",
      sizes: ["น้อยกว่า 50", "50–100", "101–500", "501–1,000", "มากกว่า 1,000"],
      timelineLabel: "ต้องการเริ่มเมื่อไร",
      timelines: ["ภายใน 1 เดือน", "1–3 เดือน", "3–6 เดือน", "ยังสำรวจข้อมูล"],
      nameLabel: "ชื่อ–นามสกุล",
      orgLabel: "องค์กร / บริษัท",
      positionLabel: "ตำแหน่ง",
      emailLabel: "อีเมล",
      phoneLabel: "โทรศัพท์",
      errorRole: "เลือกบทบาทก่อนไปขั้นถัดไป",
      errorIndustry: "เลือกอย่างน้อยหนึ่งอุตสาหกรรม",
      errorSize: "เลือกจำนวนผู้ใช้งาน",
      errorContact: "กรอกชื่อ องค์กร และอีเมลให้ครบ",
      errorEmail: "รูปแบบอีเมลไม่ถูกต้อง",
      errorSend: "ส่งผ่านเว็บไม่สำเร็จ กดปุ่มด้านล่างเพื่อส่งข้อมูลเดียวกันทางอีเมลแทนได้เลย",
      errorLocal: "โหมดพรีวิวยังส่งผ่านเว็บไม่ได้ (ต้องขึ้น Netlify ก่อน) กดปุ่มด้านล่างเพื่อส่งทางอีเมลแทน",
      mailFallback: "ส่งทางอีเมลแทน",
      mailSubject: "ขอข้อมูล Talkblox",
      doneTitle: "ได้รับข้อมูลแล้ว",
      doneBody: "ทีมงานจะนำบริบทที่คุณให้ไว้ไปออกแบบตัวอย่างการใช้งานเฉพาะธุรกิจของคุณ",
      doneSteps: [
        { n: "01", label: "ตรวจข้อมูล", body: "ทีมงานอ่านบริบทและจับคู่กับสถานการณ์ในอุตสาหกรรมของคุณ" },
        { n: "02", label: "ติดต่อกลับ", body: "ภายใน 1–2 วันทำการ ทางอีเมลหรือโทรศัพท์ที่ให้ไว้" },
        { n: "03", label: "ทดลองใช้จริง", body: "เปิดบัญชีทดลองพร้อมบทเรียนตัวอย่างที่ตรงกับงานของคุณ" },
      ],
      doneContact: "เร่งด่วนติดต่อได้ที่ 095 289 3646 หรือ info@talkblox.ai",
      doneClose: "เรียบร้อย",
    },
    why: {
      title: ["เพราะการสื่อสารที่ดี", "ต้องใช้ได้จริงในงาน"],
      body: "Talkblox ไม่ได้หยุดที่การเรียนภาษา แต่ช่วยให้คนทำงานฝึกจนพร้อมสื่อสารในสถานการณ์จริงของอาชีพ",
      cta: "ดูรายละเอียด",
      chart: {
        weeks: ["สัปดาห์ 1", "สัปดาห์ 2", "สัปดาห์ 3", "สัปดาห์ 4"],
        badge: "+20% WTC",
      },
      features: [
        {
          title: "เรียนให้ตรงกับงาน",
          body: "ฝึกจากเนื้อหาและสถานการณ์ที่ตรงกับงานของคุณ",
          media: "ผู้เรียนใช้งานบทเรียนเฉพาะอาชีพ",
        },
        {
          title: "ฝึกจากสถานการณ์จริง",
          body: "โต้ตอบกับคู่สนทนาเสมือนจริง ในสถานการณ์ที่ต้องเจอในการทำงาน",
          media: "ผู้ใช้สวมหูฟังฝึกสนทนากับ AI",
        },
        {
          title: "WTC Analytics",
          body: "ติดตามความพร้อมในการสื่อสาร",
          media: "แดชบอร์ดวัดความพร้อมในการสื่อสาร",
        },
      ],
    },
    product: {
      heading: ["ครบทุกทักษะ", "ตั้งแต่ฝึกจนวัดผล"],
      body: "ฝึกทักษะเฉพาะด้าน จำลองสถานการณ์จริง ฝึกนำเสนอ และติดตามพัฒนาการได้ในที่เดียว",
      steps: [
        {
          name: "SkillLab",
          icon: "course",
          title: "เจาะลึกทักษะที่คุณต้องการ",
          body: "พัฒนาครบ 8 ด้าน พร้อมฝึกจุดที่คนไทยมักผิดบ่อย โดยเฉพาะการออกเสียงและการใช้ภาษาในงานจริง",
        },
        {
          name: "Roleplay",
          icon: "conversation",
          title: "ฝึกสถานการณ์จริงเฉพาะอาชีพ",
          body: "ฝึกโต้ตอบในสถานการณ์ที่สร้างตามอาชีพ หน้าที่ และบริบทงานของคุณ",
        },
        {
          name: "Present",
          icon: "training",
          title: "ฝึกนำเสนออย่างมืออาชีพ",
          body: "ฝึกตามเสียงต้นแบบของคุณ พร้อมรับคำแนะนำทันที เพื่อพัฒนาการออกเสียง น้ำเสียง และจังหวะการนำเสนอ",
        },
        {
          name: "Progress",
          icon: "analytics",
          title: "เห็นพัฒนาการอย่างต่อเนื่อง",
          body: "ติดตามทักษะ ความพร้อมในการสื่อสาร และผลการฝึกได้ทั้งรายบุคคล ทีม และองค์กร",
        },
      ],
      diagramStats: [
        { value: "8", label: "ทักษะที่พัฒนา" },
        { value: "5", label: "โมดูล AI" },
        { value: "3", label: "แหล่งข้อมูลบริบท" },
        { value: "+20%", label: "WTC" },
      ],
      diagramTips: [
        "บริบทองค์กร + อาชีพ + ผู้เรียน",
        "อ่านเสียง ใบหน้า และข้อความพร้อมกัน",
        "ได้บทเรียนเฉพาะคุณ",
      ],
      diagram: "แผนภาพการทำงานของ Talkblox — ข้อมูลองค์กร เนื้อหาเฉพาะอาชีพ และโปรไฟล์ผู้เรียน ผ่าน Multimodal AI และ Personalized Corpus Engine สู่ประสบการณ์การเรียนรู้เฉพาะบุคคล",
      preview: "ภาพหน้าจอแดชบอร์ดผู้ดูแลระบบ แสดงความก้าวหน้ารายทีม",
      note: "รองรับการเชื่อมต่อกับระบบ HR และ LMS ขององค์กร",
      overlay: {
        tips: [
          "ลองพูดช้าลงอีกนิด ปลายประโยคจะชัดขึ้น",
          "เติมคำขอร้องก่อนถามข้อมูล จะฟังสุภาพขึ้น",
        ],
        mic: "กำลังฟัง...",
        feedbackLabel: "ความคล่อง",
        feedbackValue: "+12%",
        scores: [
          { label: "ความคล่อง", value: "+12%" },
          { label: "การออกเสียง", value: "+15%" },
          { label: "ความมั่นใจ", value: "+8%" },
          { label: "คำศัพท์", value: "+18%" },
          { label: "การโต้ตอบ", value: "+6%" },
        ],
      },
    },
    impact: {
      title: ["Skills Today", "Brighter Tomorrow"],
      sub: "พัฒนาคน สร้างโอกาส ขับเคลื่อนองค์กรสู่อนาคต",
      bg: "ภาพบุคคลมองทิวทัศน์ภูเขายามเช้า",
      stats: [
        { value: "30%+", line1: "WTC เพิ่มขึ้น", line2: "โดยเฉลี่ย" },
        { value: "600+", line1: "ผู้ใช้งานจริง", line2: "จาก 7 มหาวิทยาลัย" },
        { value: "หลากหลาย", line1: "รองรับหลาย", line2: "อุตสาหกรรม" },
      ],
    },
    partner: {
      title: ["ร่วมสร้างโอกาส", "ให้ไปได้ไกลกว่า", "ด้วยกัน"],
      body: "Talkblox พร้อมร่วมมือกับองค์กรและผู้ให้บริการ เพื่อพัฒนาทักษะคนทำงาน และต่อยอดโอกาสทางธุรกิจไปด้วยกัน",
      cta: "ดูข้อมูลสำหรับพันธมิตร",
      types: [
        { icon: "partner", th: "ตัวแทนจำหน่าย", en: "Reseller", role: "ตัวแทนจำหน่าย" },
        {
          icon: "training",
          th: "ผู้ให้บริการฝึกอบรม",
          en: "Training Provider",
          role: "ผู้ให้บริการฝึกอบรม",
        },
        {
          icon: "course",
          th: "ผู้พัฒนาเนื้อหา",
          en: "Content / Course Provider",
          role: "ผู้พัฒนาเนื้อหา",
        },
        {
          icon: "education",
          th: "สถาบันการศึกษา",
          en: "Education Partner",
          role: "สถาบันการศึกษา",
        },
      ],
      badge: ["White Label", "Co-Branding"],
    },
    conversion: {
      title: ["พร้อมรับข้อเสนอที่ดีที่สุดจากเรา", "ตอนนี้"],
      sub: "เริ่มต้นกับ Talkblox และออกแบบการฝึกให้เหมาะกับองค์กรของคุณ",
      cta: "พูดคุยกับเรา",
      script: "Real Skills\nReal People\nBrighter Opportunities",
    },
    footer: {
      copyright: ["© 2026 บริษัท ทอล์กบล็อกซ์ จำกัด", "All rights reserved."],
      navLabel: "เมนูส่วนท้าย",
    },
  },

  en: {
    langLabel: "EN",
    langSwitchTo: "Switch to Thai",
    skip: "Skip to main content",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    home: "Talkblox home",
    nav: [
      { label: "Home", href: "#top" },
      { label: "Product", href: "#product" },
      { label: "Solutions", href: "#industry" },
      { label: "Partners", href: "#partner" },
      { label: "About", href: "#why" },
    ],
    ctaShort: "Start a trial",
    hero: {
      eyebrow: "Real skills. Real people. Brighter opportunities.",
      headline: ["Occupational communication skills,", "built around you"],
      support: "Built on multimodal artificial intelligence",
      english: "Multimodal AI Driven Occupational Communication Skills Platform",
      benefits: [
        { icon: "target", line1: "Lessons built", line2: "around each role" },
        { icon: "conversation", line1: "Practice in", line2: "real situations" },
        { icon: "analytics", line1: "Measurable", line2: "visible progress" },
      ],
      primaryCta: "Start a trial",
      secondaryCta: "Watch the intro",
      script: "Skills\nCreate\nOpportunities",
      portrait: "Working professional looking ahead, city backdrop",
      people: ["Hotel front desk staff", "Site engineer", "Healthcare worker"],
      appAvatar: "Practice partner shown inside a lesson",
      floatCard: ["Practice anywhere,", "any time"],
      phone: ["Learn", "Practice", "Perform"],
      tips: [
        "Clearer /θ/ sound, up 15%",
        "Responses 1.2s faster",
        "3 more polite forms used",
      ],
      radar: {
        title: "Willingness to Communicate",
        unit: "WTC score",
        axes: [
          { label: "Confidence", value: 82 },
          { label: "Fluency", value: 68 },
          { label: "Accent", value: 74 },
          { label: "Vocabulary", value: 79 },
          { label: "Turn-taking", value: 71 },
        ],
      },
      levels: {
        title: "Skill level by week",
        score: "71",
        unit: "out of 100",
        bars: [42, 55, 63, 71, 84],
      },
    },
    industry: {
      heading: ["Learn in the real context", "of your own work"],
      lead: "Occupational content that adapts to the learner and the organisation, practised anywhere, any time.",
      script: "Every job\nopens further",
      scriptSub: "once you speak with confidence",
      label: "Industries we support",
      items: [
        { icon: "hospitality", th: ["Tourism", "and hospitality"], en: "Hospitality" },
        { icon: "logistics", th: ["Logistics", "and trade"], en: "Logistics" },
        { icon: "healthcare", th: ["Medical", "and health"], en: "Healthcare" },
        { icon: "business", th: ["Services and", "global organisations"], en: "Business" },
      ],
      mediaPrefix: "Workplace context in",
      cta: "Tell us your industry",
    },
    lead: {
      open: "Tell us your industry",
      close: "Close dialog",
      title: "Shape the training around your business",
      sub: "A few questions, and the team will come back with examples that fit your context.",
      stepLabel: "Step",
      of: "of",
      back: "Back",
      next: "Next",
      submit: "Send",
      sending: "Sending...",
      required: "required",
      steps: [
        { title: "Tell us what kind of business you are", hint: "So we understand your context" },
        { title: "What context is your business in?", hint: "Choose one or more" },
        { title: "How can the team reach you?", hint: "Used only to contact you about Talkblox" },
      ],
      roles: [
        { value: "Government agency", en: "Public Sector", icon: "hospitality" },
        { value: "Private company", en: "Private Sector", icon: "business" },
        { value: "Reseller", en: "Reseller", icon: "partner" },
        { value: "Training provider", en: "Training Provider", icon: "training" },
        { value: "Content provider", en: "Content / Course Provider", icon: "course" },
        { value: "Education partner", en: "Education Partner", icon: "education" },
      ],
      industryLabel: "Industry",
      industries: [
        "Tourism & hospitality",
        "Logistics & trade",
        "Medical & health",
        "Business services",
        "Global organisations",
        "Manufacturing",
        "Retail",
        "Finance & insurance",
        "Technology",
        "Government & state enterprise",
        "Education",
        "Other",
      ],
      businessLabel: "What is the core business?",
      businessPlaceholder: "e.g. two 4-star hotels, mostly international guests",
      sizeLabel: "Approximate number of users",
      sizes: ["Under 50", "50–100", "101–500", "501–1,000", "Over 1,000"],
      timelineLabel: "When would you start?",
      timelines: ["Within a month", "1–3 months", "3–6 months", "Still exploring"],
      nameLabel: "Full name",
      orgLabel: "Organisation",
      positionLabel: "Role",
      emailLabel: "Email",
      phoneLabel: "Phone",
      errorRole: "Choose a role to continue",
      errorIndustry: "Choose at least one industry",
      errorSize: "Choose a team size",
      errorContact: "Name, organisation and email are required",
      errorEmail: "That email does not look right",
      errorSend: "Web submission failed — use the button below to send the same details by email",
      errorLocal: "Preview mode cannot submit yet (needs a Netlify deploy). Use the button below to send by email instead.",
      mailFallback: "Send by email instead",
      mailSubject: "Talkblox enquiry",
      doneTitle: "We have your details",
      doneBody: "The team will use what you shared to build examples specific to your business.",
      doneSteps: [
        { n: "01", label: "We review", body: "Your context is matched against scenarios from your industry" },
        { n: "02", label: "We reach out", body: "Within one to two working days, by email or phone" },
        { n: "03", label: "You try it", body: "A trial account with sample lessons built around your work" },
      ],
      doneContact: "Need it sooner? Call 095 289 3646 or email info@talkblox.ai",
      doneClose: "Done",
    },
    why: {
      title: ["Good communication", "has to hold up at work"],
      body: "Talkblox does not stop at language learning — it takes people through to being ready for the real situations their job brings.",
      cta: "See how it works",
      chart: {
        weeks: ["Week 1", "Week 2", "Week 3", "Week 4"],
        badge: "+20% WTC",
      },
      features: [
        {
          title: "Learn what the job needs",
          body: "Content and situations drawn from the work you actually do",
          media: "Learner working through a role-based lesson",
        },
        {
          title: "Practise real situations",
          body: "Talk with a realistic partner in the situations your work brings",
          media: "User in headphones practising with AI",
        },
        {
          title: "WTC Analytics",
          body: "Track communication readiness over time",
          media: "Dashboard showing communication readiness",
        },
      ],
    },
    product: {
      heading: ["Every skill,", "from practice to evidence"],
      body: "Targeted skill work, realistic simulation, presentation practice and progress tracking — all in one place.",
      steps: [
        {
          name: "SkillLab",
          icon: "course",
          title: "Go deep on the skills you need",
          body: "All eight areas, with focused work on the errors Thai speakers make most — especially pronunciation and workplace usage",
        },
        {
          name: "Roleplay",
          icon: "conversation",
          title: "Rehearse situations from your own role",
          body: "Interactive practice built around your occupation, responsibilities and working context",
        },
        {
          name: "Present",
          icon: "training",
          title: "Present like a professional",
          body: "Practise against a model of your own voice, with immediate guidance on pronunciation, tone and pacing",
        },
        {
          name: "Progress",
          icon: "analytics",
          title: "See progress continuously",
          body: "Skills, communication readiness and practice results — per person, per team and across the organisation",
        },
      ],
      diagramStats: [
        { value: "8", label: "skill areas" },
        { value: "5", label: "AI modules" },
        { value: "3", label: "context sources" },
        { value: "+20%", label: "WTC" },
      ],
      diagramTips: [
        "Organisation + role + learner",
        "Voice, face and text read together",
        "A course built for you",
      ],
      diagram: "How Talkblox works — organisation data, occupational content and learner profile flow through multimodal AI and the personalised corpus engine into an individual learning experience",
      preview: "Admin dashboard screenshot showing progress by team",
      note: "Connects to your existing HR and LMS systems",
      overlay: {
        tips: [
          "Try slowing down — your sentence endings will land clearer",
          "Add a softener before the request to sound warmer",
        ],
        mic: "Listening...",
        feedbackLabel: "Fluency",
        feedbackValue: "+12%",
        scores: [
          { label: "Fluency", value: "+12%" },
          { label: "Accent", value: "+15%" },
          { label: "Confidence", value: "+8%" },
          { label: "Vocabulary", value: "+18%" },
          { label: "Turn-taking", value: "+6%" },
        ],
      },
    },
    impact: {
      title: ["Skills Today", "Brighter Tomorrow"],
      sub: "Develop people, open opportunities, move the organisation forward",
      bg: "Person looking out over mountains at sunrise",
      stats: [
        { value: "30%+", line1: "Average rise", line2: "in WTC" },
        { value: "600+", line1: "Active learners", line2: "across 7 universities" },
        { value: "Multiple", line1: "Industries", line2: "supported" },
      ],
    },
    partner: {
      title: ["Build opportunities", "that reach further", "together"],
      body: "Talkblox works with organisations and service providers to raise workforce communication skills and grow the business opportunity together.",
      cta: "Partner information",
      types: [
        { icon: "partner", th: "Reseller", en: "Reseller", role: "Reseller" },
        {
          icon: "training",
          th: "Training provider",
          en: "Training Provider",
          role: "Training provider",
        },
        {
          icon: "course",
          th: "Content provider",
          en: "Content / Course Provider",
          role: "Content provider",
        },
        {
          icon: "education",
          th: "Institution",
          en: "Education Partner",
          role: "Education partner",
        },
      ],
      badge: ["White Label", "Co-Branding"],
    },
    conversion: {
      title: ["Get our best offer", "right now"],
      sub: "Start with Talkblox and shape the training around your organisation.",
      cta: "Talk to us",
      script: "Real Skills\nReal People\nBrighter Opportunities",
    },
    footer: {
      copyright: ["© 2026 Talkblox Co., Ltd.", "All rights reserved."],
      navLabel: "Footer menu",
    },
  },
} as const;

type Copy = (typeof copy)["th"];

/* ── ภาพประกอบ ──────────────────────────────────────────────────────────────
   ค่าจริงอยู่ใน Images.ts (ฝัง base64 ไว้เพื่อให้รันได้ทันทีทุกที่)
   เมื่อย้ายไปใช้ไฟล์ใน assets/ แล้ว แก้ที่ Images.ts ที่เดียว
   ตารางนี้และ component ทั้งหมดไม่ต้องแตะ                                    */
const img = {
  heroPortrait: IMG.heroPortrait,
  heroPortraitSet: IMG_SET.heroPortrait,
  people: [IMG.personHospitality, IMG.personLogistics, IMG.personHealthcare],
  peopleSet: IMG_SET.people,
  industry: {
    hospitality: IMG.industryHospitality,
    logistics: IMG.industryLogistics,
    healthcare: IMG.industryHealthcare,
    business: IMG.industryBusiness,
  } as Record<string, string>,
  industrySet: IMG_SET.industry,
  features: [IMG.featureLesson, IMG.featurePractice],
  featureSet: IMG_SET.features,
  productDashboard: IMG.productDashboard,
  productDashboardSet: IMG_SET.productDashboard,
  productArchitecture: IMG.productArchitecture,
  productArchitectureSet: IMG_SET.productArchitecture,
  productArchitectureEn: IMG.productArchitectureEn,
  productArchitectureEnSet: IMG_SET.productArchitectureEn,
};

/* ค่าที่ไม่ขึ้นกับภาษา */
const contacts = [
  { icon: "phone", text: "095 289 3646", href: "tel:+66952893646" },
  { icon: "mail", text: "info@talkblox.ai", href: "mailto:info@talkblox.ai" },
  { icon: "pin", text: "88/203 บางชัน คลองสามวา กรุงเทพฯ 10510", href: undefined },
];

const socials = [
  { icon: "line", label: "LINE Official Account", href: "#" },
  { icon: "facebook", label: "Facebook", href: "#" },
  { icon: "linkedin", label: "LinkedIn", href: "#" },
  { icon: "youtube", label: "YouTube", href: "#" },
];

/* ปุ่มหลายจุดในหน้าต้องเปิดแผงคัดกรองอันเดียวกัน จึงยก state ขึ้นไปที่ App
   แล้วส่งฟังก์ชันเปิดลงมาผ่าน context แทนการมีแผงซ้ำหลายชุด            */
const LeadContext = createContext<{ openLead: (role?: string) => void }>({
  openLead: () => {},
});

const useLead = () => useContext(LeadContext);

const LangContext = createContext<{ lang: Lang; t: Copy; toggle: () => void }>({
  lang: "th",
  t: copy.th as unknown as Copy,
  toggle: () => {},
});

const useT = () => useContext(LangContext);

/* ------------------------------------------------------------------ icons -- */
/* ใช้ Heroicons v2 (outline) เป็น Icon Family เดียวทั้งเว็บไซต์ตาม §16
   override strokeWidth เป็น 1.9 เพื่อให้อยู่ในช่วง 1.75–2 ที่กำหนด
   โลโก้โซเชียลไม่มีใน Heroicons จึงแยกเป็น <SocialIcon /> ต่างหาก        */

import {
  AcademicCapIcon,
  ArrowRightIcon,
  Bars3Icon,
  BriefcaseIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  HeartIcon,
  MapPinIcon,
  MicrophoneIcon,
  BuildingOffice2Icon,
  PhoneIcon,
  PlayIcon,
  PresentationChartBarIcon,
  TruckIcon,
  UserGroupIcon,
  ViewfinderCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

type IconComponent = typeof ViewfinderCircleIcon;

const icons: Record<string, IconComponent> = {
  target: ViewfinderCircleIcon,
  conversation: ChatBubbleLeftRightIcon,
  analytics: ChartBarIcon,
  microphone: MicrophoneIcon,
  play: PlayIcon,
  arrow: ArrowRightIcon,
  hospitality: BuildingOffice2Icon,
  logistics: TruckIcon,
  healthcare: HeartIcon,
  business: BriefcaseIcon,
  partner: UserGroupIcon,
  training: PresentationChartBarIcon,
  course: DocumentTextIcon,
  education: AcademicCapIcon,
  globe: GlobeAltIcon,
  phone: PhoneIcon,
  mail: EnvelopeIcon,
  pin: MapPinIcon,
  menu: Bars3Icon,
  close: XMarkIcon,
};

function Icon({ name, size = 24 }: { name: string; size?: number }) {
  const Glyph = icons[name];
  if (!Glyph) return null;
  return (
    <Glyph
      className="icon"
      width={size}
      height={size}
      strokeWidth={1.9}
      aria-hidden="true"
      focusable="false"
    />
  );
}

/* โลโก้แบรนด์โซเชียล — วาดเองเพราะเป็น trademark ไม่ใช่ UI icon */
const socialPaths: Record<string, React.ReactNode> = {
  line: (
    <path d="M21 10.4c0-4-4-7.3-9-7.3s-9 3.3-9 7.3c0 3.6 3.2 6.6 7.5 7.2.3.1.7.2.8.5.1.3.1.7 0 1l-.1.8c0 .3-.2 1 .9.6s5.9-3.5 8-6c1.4-1.5 2-3 2-4.1Z" />
  ),
  facebook: (
    <path d="M14.6 21.2v-8h2.7l.4-3.1h-3.1V8.1c0-.9.3-1.5 1.6-1.5h1.6V3.8c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.3H8.7v3.1h2.7v8" />
  ),
  linkedin: (
    <>
      <rect x="3.2" y="3.2" width="17.6" height="17.6" rx="3" />
      <path d="M7.6 10.6v6.2M7.6 7.6v.01M11.6 16.8v-6.2M11.6 13.2c0-1.5 1-2.6 2.4-2.6s2.4 1 2.4 2.6v3.6" />
    </>
  ),
  youtube: (
    <>
      <rect x="2.6" y="5.6" width="18.8" height="12.8" rx="3.6" />
      <path d="m10.4 9.4 4.6 2.6-4.6 2.6V9.4Z" />
    </>
  ),
};

function SocialIcon({ name, size = 20 }: { name: string; size?: number }) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {socialPaths[name]}
    </svg>
  );
}

/* --------------------------------------------------------------- primitives */

function Logo({ className = "" }: { className?: string }) {
  return (
    <img
      src={LOGO_SRC}
      srcSet={LOGO_SRCSET}
      sizes="(max-width: 767px) 140px, 160px"
      alt="Talkblox"
      className={`logo__img ${className}`}
      width={640}
      height={192}
      decoding="async"
    />
  );
}

type MediaProps = {
  /** ใส่ path รูปจริงเมื่อพร้อม — ถ้าไม่ใส่จะ render placeholder */
  src?: string;
  srcSet?: string;
  sizes?: string;
  alt?: string;
  /** คำอธิบายภาพที่ต้องการ ใช้เป็น alt สำรองและแสดงบน placeholder */
  label: string;
  ratio?: string;
  className?: string;
  rounded?: boolean;
  /** ภาพที่อยู่เหนือ fold ให้ตั้ง priority เพื่อไม่ lazy load (§22) */
  priority?: boolean;
};

function Media({
  src,
  srcSet,
  sizes,
  alt,
  label,
  ratio = "4 / 3",
  className = "",
  rounded = true,
  priority = false,
}: MediaProps) {
  const style = { aspectRatio: ratio } as React.CSSProperties;
  if (src) {
    // React 18.1 ยังไม่รู้จัก prop `fetchPriority` แบบ camelCase
    // จึงส่งเป็น attribute ตรง ๆ เพื่อไม่ให้ขึ้น warning
    const priorityAttrs = priority ? ({ fetchpriority: "high" } as Record<string, string>) : {};
    return (
      <img
        src={src}
        srcSet={srcSet}
        sizes={srcSet ? sizes : undefined}
        alt={alt ?? label}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className={`media ${rounded ? "media--rounded" : ""} ${className}`}
        style={style}
        {...priorityAttrs}
      />
    );
  }
  return (
    <div
      className={`media media--placeholder ${rounded ? "media--rounded" : ""} ${className}`}
      style={style}
      role="img"
      aria-label={label}
    >
      <span className="media__label">{label}</span>
    </div>
  );
}

type ButtonProps = {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline" | "text";
  size?: "md" | "lg";
  href?: string;
  icon?: "arrow" | "play" | null;
  iconPosition?: "start" | "end";
  className?: string;
  onClick?: () => void;
};

function Button({
  children,
  variant = "primary",
  size = "md",
  href,
  icon = null,
  iconPosition = "end",
  className = "",
  onClick,
}: ButtonProps) {
  const cls = `btn btn--${variant} btn--${size} ${className}`;
  const inner = (
    <>
      {icon && iconPosition === "start" && (
        <span className="btn__icon btn__icon--start">
          <Icon name={icon} size={18} />
        </span>
      )}
      <span className="btn__label">{children}</span>
      {icon && iconPosition === "end" && (
        <span className="btn__icon btn__icon--end">
          <Icon name={icon} size={18} />
        </span>
      )}
    </>
  );
  if (href) {
    return (
      <a className={cls} href={href} onClick={onClick}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      {inner}
    </button>
  );
}

/** เข้าสายตาแล้วหรือยัง — ปล่อยค่า true ครั้งเดียวแล้วเลิกเฝ้า
    ทำให้ animation ของตัวเลขและกราฟเล่นรอบเดียวจนกว่าจะรีเฟรช (§18) */
function useInView<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      // ขยับขอบล่างขึ้นมา 12% เพื่อให้ animation เริ่มตอนองค์ประกอบเข้ามาในสายตาจริง
      // ไม่ใช่ตอนเพิ่งโผล่พ้นขอบจอ ซึ่งผู้ใช้จะพลาดช่วงต้นของการเคลื่อนไหว
      { threshold, rootMargin: "0px 0px -12% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, inView] as const;
}

/** นับเลขขึ้นจาก 0 ถึงค่าจริง ease-out — เล่นรอบเดียวเมื่อ run เป็น true */
function useCountUp(target: number, run: boolean, duration = 1800) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!run) return;
    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      // expo-out ทำให้ตัวเลขพุ่งเร็วตอนต้นแล้วค่อย ๆ นิ่ง ลื่นกว่า cubic
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target, duration]);

  return value;
}

/** ตัวเลข KPI — แยกส่วนตัวเลขออกมานับขึ้น ส่วนที่ไม่ใช่ตัวเลขแสดงตามเดิม */
function CountUpValue({ text }: { text: string }) {
  const [ref, inView] = useInView<HTMLSpanElement>(0.5);
  const parts = text.match(/^(\D*)(\d[\d,]*)(.*)$/);
  const target = parts ? parseInt(parts[2].replace(/,/g, ""), 10) : 0;
  const n = useCountUp(target, inView && parts !== null);

  return (
    <span ref={ref} className="countup">
      {parts ? `${parts[1]}${n.toLocaleString()}${parts[3]}` : text}
    </span>
  );
}

/** ตัดข้อความเป็น grapheme ไม่ใช่ code point
    ภาษาไทยมีสระและวรรณยุกต์เป็นอักขระแยก ถ้าตัดทีละ code point
    จะเห็นวรรณยุกต์ลอยเดี่ยว ๆ ระหว่างพิมพ์ Intl.Segmenter แก้ปัญหานี้ให้ */
function splitGraphemes(text: string): string[] {
  const Seg = (Intl as any).Segmenter;
  if (typeof Seg === "function") {
    const seg = new Seg("th", { granularity: "grapheme" });
    return Array.from(seg.segment(text), (s: any) => s.segment);
  }
  return Array.from(text);
}

/** พิมพ์ข้อความทีละตัว เล่นรอบเดียว แล้วเรียก onDone เพื่อส่งต่อบรรทัดถัดไป */
function Typing({
  text,
  run,
  speed = 55,
  onDone,
  className = "",
  loop = false,
  pause = 5000,
}: {
  text: string;
  run: boolean;
  speed?: number;
  onDone?: () => void;
  className?: string;
  /** พิมพ์จบแล้วพักตามค่า pause จากนั้นลบทิ้งแล้วพิมพ์ใหม่ */
  loop?: boolean;
  pause?: number;
}) {
  const [count, setCount] = useState(0);
  const chars = splitGraphemes(text);
  const done = count >= chars.length;
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!run) return;
    if (prefersReducedMotion()) {
      setCount(chars.length);
      doneRef.current?.();
      return;
    }
    let i = 0;
    let interval = 0;
    let timer = 0;

    const start = () => {
      i = 0;
      setCount(0);
      interval = window.setInterval(() => {
        i += 1;
        setCount(i);
        if (i >= chars.length) {
          window.clearInterval(interval);
          doneRef.current?.();
          if (loop) timer = window.setTimeout(start, pause);
        }
      }, speed);
    };

    start();
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timer);
    };
  }, [run, text, speed, chars.length, loop, pause]);

  return (
    <span className={`typing ${done ? "is-done" : ""} ${className}`} aria-hidden="true">
      {/* ghost จองพื้นที่ไว้เท่าข้อความเต็ม ป้องกัน layout ขยับระหว่างพิมพ์ */}
      <span className="typing__ghost">{text}</span>
      <span className="typing__live">
        {chars.slice(0, count).join("")}
        {run && !done && <i className="typing__caret" />}
      </span>
    </span>
  );
}

/** เอฟเฟกต์รอบแผนภาพสถาปัตยกรรม
    โครงข่ายเส้นเชื่อม ฝุ่นดาว และไอสี วาดด้วย SVG/CSS ทั้งหมด
    ไม่ได้แตะไฟล์ภาพต้นฉบับ จึงเปลี่ยนภาพเมื่อไหร่ก็ยังใช้ได้              */
function DiagramFx() {
  // โหนดวางรอบขอบภาพ ไม่ทับเนื้อหาตรงกลางที่มีตัวหนังสือหนาแน่น
  const nodes: readonly (readonly [number, number])[] = [
    [3, 10], [11, 3], [26, 8], [44, 2], [58, 6], [74, 3], [88, 8],
    [97, 16], [98, 34], [93, 49], [80, 56], [62, 54], [44, 57], [27, 53],
    [12, 50], [2, 40], [5, 25],
  ];
  const links: readonly (readonly [number, number])[] = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8],
    [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [14, 15],
    [15, 16], [16, 0], [2, 13], [5, 11], [8, 12], [1, 16],
  ];

  return (
    <>
      {/* ชั้นหลังภาพ — ไอสีเรืองเป็นรัศมีรอบแผนภาพ */}
      <div className="dgfx dgfx--back" aria-hidden="true">
        <span className="dgfx__aura dgfx__aura--1" />
        <span className="dgfx__aura dgfx__aura--2" />
        <span className="dgfx__aura dgfx__aura--3" />
        <span className="dgfx__aura dgfx__aura--4" />
        <span className="dgfx__aura dgfx__aura--5" />
      </div>

      {/* ชั้นหน้าภาพ — โครงข่ายกับฝุ่นดาวต้องอยู่บนภาพถึงจะมองเห็น
          วางไว้หลังภาพเหมือนเดิมจะถูกบังจนหมด */}
      <div className="dgfx dgfx--front" aria-hidden="true">
        <svg className="dgfx__net" viewBox="0 0 100 58" preserveAspectRatio="none">
          {links.map(([a, b], i) => (
            <line
              key={i}
              className="dgfx__link"
              x1={nodes[a][0]}
              y1={nodes[a][1]}
              x2={nodes[b][0]}
              y2={nodes[b][1]}
              style={{ animationDelay: `${i * 220}ms` }}
            />
          ))}
          {nodes.map(([x, y], i) => (
            <circle
              key={i}
              className="dgfx__node"
              cx={x}
              cy={y}
              r={0.6}
              style={{ animationDelay: `${i * 170}ms` }}
            />
          ))}
        </svg>

        <span className="dgfx__dust">
          {Array.from({ length: 16 }).map((_, i) => (
            <i
              key={i}
              style={{
                left: `${(i * 29 + (i % 5) * 7) % 100}%`,
                top: `${(i * 47 + (i % 3) * 11) % 100}%`,
                animationDelay: `${(i % 11) * 380}ms`,
                animationDuration: `${4 + (i % 4)}s`,
              }}
            />
          ))}
        </span>
      </div>
    </>
  );
}

/** ประกายสี่แฉกวิบวับ — ใช้ประดับกราฟให้ดูมีชีวิต ไม่ได้สื่อความหมายเชิงข้อมูล */
function Sparks({
  points,
  from = 1200,
}: {
  points: readonly (readonly [number, number, number])[];
  from?: number;
}) {
  return (
    <g aria-hidden="true">
      {points.map(([x, y, r], i) => (
        <path
          key={i}
          className="spark"
          style={{ animationDelay: `${from + i * 420}ms` }}
          d={`M${x} ${y - r} Q${x + r * 0.18} ${y - r * 0.18} ${x + r} ${y}
              Q${x + r * 0.18} ${y + r * 0.18} ${x} ${y + r}
              Q${x - r * 0.18} ${y + r * 0.18} ${x - r} ${y}
              Q${x - r * 0.18} ${y - r * 0.18} ${x} ${y - r} Z`}
        />
      ))}
    </g>
  );
}

/** เรดาร์ 5 ด้าน — รูปขยายจากจุดกึ่งกลางออกไปจนเต็มค่าจริง เล่นรอบเดียว */
function RadarChart({
  axes,
  label,
}: {
  axes: readonly { label: string; value: number }[];
  label: string;
}) {
  const [ref, inView] = useInView<HTMLDivElement>(0.3);
  const size = 168;
  const c = size / 2;
  const r = 52;
  const step = (Math.PI * 2) / axes.length;

  const point = (i: number, ratio: number) => {
    const a = -Math.PI / 2 + i * step;
    return [c + Math.cos(a) * r * ratio, c + Math.sin(a) * r * ratio] as const;
  };
  const poly = (ratio: number) =>
    axes.map((_, i) => point(i, ratio).join(" ")).join(", ");
  const shape = axes.map((ax, i) => point(i, ax.value / 100).join(" ")).join(", ");

  return (
    <div ref={ref} className={`radar ${inView ? "is-drawn" : ""}`}>
      <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label}>
        <defs>
          <radialGradient id="tbxRadarFill">
            <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.45" />
            <stop offset="100%" stopColor="var(--brand-secondary)" stopOpacity="0.28" />
          </radialGradient>
        </defs>

        {[0.35, 0.7, 1].map((ratio) => (
          <polygon key={ratio} className="radar__web" points={poly(ratio)} />
        ))}
        {axes.map((_, i) => {
          const p = point(i, 1);
          return <line key={i} className="radar__spoke" x1={c} y1={c} x2={p[0]} y2={p[1]} />;
        })}

        <polygon className="radar__shape" points={shape} />

        {/* วงขยายรอบตัวเองทีละแกน เล่นรอบเดียวหลังรูปทรงกางเสร็จ */}
        {axes.map((ax, i) => {
          const p = point(i, ax.value / 100);
          return (
            <circle
              key={`pulse-${ax.label}`}
              className="radar__pulse"
              cx={p[0]}
              cy={p[1]}
              r={4}
              style={{ animationDelay: `${1700 + i * 320}ms` }}
            />
          );
        })}

        {axes.map((ax, i) => {
          const p = point(i, ax.value / 100);
          return (
            <circle
              key={ax.label}
              className="radar__dot"
              cx={p[0]}
              cy={p[1]}
              r={3}
              style={{ animationDelay: `${640 + i * 90}ms` }}
            />
          );
        })}

        <Sparks
          points={[
            [34, 30, 3.4],
            [136, 44, 2.6],
            [26, 122, 2.8],
            [144, 126, 3.2],
            [84, 16, 2.4],
          ]}
          from={1500}
        />

        {axes.map((ax, i) => {
          const p = point(i, 1.3);
          return (
            <text
              key={ax.label}
              className="radar__label"
              x={p[0]}
              y={p[1]}
              textAnchor={p[0] > c + 6 ? "start" : p[0] < c - 6 ? "end" : "middle"}
              dominantBaseline="middle"
            >
              {ax.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

/** บาร์ระดับทักษะ — แท่งโตขึ้นจากฐานทีละแท่ง เล่นรอบเดียว */
function LevelBars({ bars, label }: { bars: readonly number[]; label: string }) {
  const [ref, inView] = useInView<HTMLDivElement>(0.3);
  return (
    <div ref={ref} className={`levels ${inView ? "is-drawn" : ""}`} role="img" aria-label={label}>
      {bars.map((v, i) => (
        <span key={i} className="levels__bar">
          <span
            className="levels__fill"
            // ไล่ทีละแท่งจากซ้ายไปขวา ระยะห่างมากพอให้ตาไล่ทัน
            style={{ height: `${v}%`, animationDelay: `${240 + i * 280}ms` }}
          />
        </span>
      ))}
    </div>
  );
}

/** กราฟพัฒนาการ WTC รายสัปดาห์
    เส้นวาดจากซ้ายไปขวา พื้นใต้เส้นจางขึ้น จุดเด้งทีละสัปดาห์
    แล้วปิดท้ายด้วยป้ายผลลัพธ์ที่สัปดาห์สุดท้าย                              */
function GrowthChart({
  label,
  weeks,
  badge,
}: {
  label: string;
  weeks: readonly string[];
  badge: string;
}) {
  const [ref, inView] = useInView<HTMLDivElement>(0.3);

  // เส้นโค้งแบบ exponential — ช่วงต้นไต่ช้า ปลายพุ่งชัน
  // สื่อว่าพอผ่านจุดหนึ่งแล้วพัฒนาการเร่งตัวขึ้นเอง ไม่ใช่โตแบบเส้นตรง
  const data = [26, 38, 58, 96];
  const w = 440;
  const h = 150;
  const pad = { left: 34, right: 34, top: 24, bottom: 24 };
  const stepX = (w - pad.left - pad.right) / (data.length - 1);
  const scaleY = (v: number) =>
    h - pad.bottom - ((v - 20) / 80) * (h - pad.top - pad.bottom);

  const pts = data.map((v, i) => [pad.left + i * stepX, scaleY(v)] as const);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ");
  const area = `${line} L${pts[pts.length - 1][0]} ${h - pad.bottom} L${pts[0][0]} ${h - pad.bottom} Z`;

  return (
    <div ref={ref} className={`chart ${inView ? "is-drawn" : ""}`}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label}>
        <defs>
          <linearGradient id="tbxLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--brand-secondary)" />
            <stop offset="100%" stopColor="var(--brand-primary)" />
          </linearGradient>
          <linearGradient id="tbxArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0, 1, 2, 3].map((i) => (
          <line
            key={i}
            className="chart__grid"
            x1={pad.left}
            x2={w - pad.right}
            y1={pad.top + i * ((h - pad.top - pad.bottom) / 3)}
            y2={pad.top + i * ((h - pad.top - pad.bottom) / 3)}
          />
        ))}

        <path className="chart__area" d={area} fill="url(#tbxArea)" />
        <path className="chart__line" d={line} pathLength={1} stroke="url(#tbxLine)" />

        <Sparks
          points={[
            [72, 40, 3],
            [246, 34, 2.4],
            [172, 116, 2.6],
            [392, 88, 2.2],
          ]}
          from={1900}
        />

        {pts.map((p, i) => (
          <circle
            key={i}
            className={`chart__dot ${i === pts.length - 1 ? "is-last" : ""}`}
            cx={p[0]}
            cy={p[1]}
            r={i === pts.length - 1 ? 5 : 3.6}
            // ค่าแรกคือจังหวะเด้งตอนเข้าฉาก ค่าที่สองคือรอบไล่กระพริบ 1→4
            style={{ animationDelay: `${700 + i * 380}ms, ${2600 + i * 900}ms` }}
          />
        ))}

        {/* ป้ายสัปดาห์ติดอยู่ใต้จุดของตัวเอง ไม่ใช่แถวแกนด้านล่าง */}
        {weeks.map((wk, i) => (
          <text
            key={wk}
            className="chart__week"
            x={pts[i][0]}
            y={pts[i][1] + 17}
            textAnchor="middle"
            style={{ animationDelay: `${800 + i * 380}ms, ${2600 + i * 900}ms` }}
          >
            {wk}
          </text>
        ))}
      </svg>

      {/* ป้ายผลลัพธ์โผล่ที่สัปดาห์สุดท้าย แล้วกระพริบค้างไว้ */}
      <span className="chart__badge">
        <CountUpValue text={badge} />
      </span>
    </div>
  );
}

/** Entrance animation กลาง — เคารพ prefers-reduced-motion (§18) */
function Reveal({
  children,
  as: Tag = "div",
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  as?: any;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

function SectionHeading({
  lines,
  accentLine,
  className = "",
}: {
  lines: readonly string[];
  accentLine?: number;
  className?: string;
}) {
  return (
    <h2 className={`section-heading ${className}`}>
      {lines.map((line, i) => (
        <span key={i} className={`section-heading__line ${i === accentLine ? "is-accent" : ""}`}>
          {line}
        </span>
      ))}
    </h2>
  );
}

/* ----------------------------------------------------------------- header -- */

/** ตามดูว่า section ไหนอยู่กลางจอ เพื่อไฮไลต์เมนูให้ตรงกัน */
function useActiveSection(hrefs: string[]) {
  const [active, setActive] = useState(hrefs[0]);
  const key = hrefs.join(",");

  useEffect(() => {
    const els = hrefs
      .map((h) => document.getElementById(h.slice(1)))
      .filter(Boolean) as HTMLElement[];
    if (!els.length) return;

    let raf = 0;

    const update = () => {
      raf = 0;
      // เส้นอ้างอิงใต้ header — section สุดท้ายที่ผ่านเส้นนี้ไปแล้วคือ section
      // ที่กำลังอ่าน วิธีนี้เดินหน้าเดียวตามการเลื่อน จึงไม่กระโดดสลับไปมา
      // แบบที่ IntersectionObserver ทำเมื่อสองส่วนคาบเกี่ยวกันพอดี
      const line = window.innerHeight * 0.3;
      let current = "#" + els[0].id;
      for (const el of els) {
        if (el.getBoundingClientRect().top <= line) current = "#" + el.id;
      }
      // ถึงท้ายหน้าแล้วให้ยึด section สุดท้ายไว้ ไม่ค้างที่อันก่อนหน้า
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4;
      if (atBottom) current = "#" + els[els.length - 1].id;

      setActive(current);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [key]);

  return active;
}

function Header() {
  const { t, lang, toggle } = useT();
  const { openLead } = useLead();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(t.nav.map((n) => n.href));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("is-locked", open);
    return () => document.body.classList.remove("is-locked");
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className={`header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="container header__inner">
        <a className="header__logo" href="#top" aria-label={t.home}>
          <Logo />
        </a>

        <nav className="nav" aria-label={lang === "th" ? "เมนูหลัก" : "Main menu"}>
          <ul className="nav__list">
            {t.nav.map((item) => (
              <li key={item.href}>
                <a
                  className={`nav__link ${active === item.href ? "is-active" : ""}`}
                  href={item.href}
                  aria-current={active === item.href ? "true" : undefined}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="header__actions">
          <button type="button" className="lang" onClick={toggle} aria-label={t.langSwitchTo}>
            <Icon name="globe" size={18} />
            <span>{t.langLabel}</span>
          </button>
          <Button icon="arrow" className="header__cta" onClick={openLead}>
            {t.ctaShort}
          </Button>
          <button
            type="button"
            className="header__burger"
            aria-label={open ? t.menuClose : t.menuOpen}
            aria-expanded={open}
            aria-controls="mobile-drawer"
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name={open ? "close" : "menu"} />
          </button>
        </div>
      </div>
    </header>

      {/* drawer ต้องอยู่นอก <header> — header มี backdrop-filter ซึ่งทำให้
          position: fixed ของลูกอ้างอิงกับ header แทนที่จะเป็นหน้าจอ
          ผลคือ panel ยุบเหลือแถบบาง ๆ พร้อม scrollbar แทนที่จะเต็มจอ      */}
      {open && (
        <button
          type="button"
          className="drawer-veil"
          aria-label={t.menuClose}
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        id="mobile-drawer"
        className={`drawer ${open ? "is-open" : ""}`}
        hidden={!open}
        aria-label={lang === "th" ? "เมนูบนอุปกรณ์พกพา" : "Mobile menu"}
      >
        <div className="drawer__head">
          <Logo />
          <button
            type="button"
            className="drawer__close"
            aria-label={t.menuClose}
            onClick={() => setOpen(false)}
          >
            <Icon name="close" />
          </button>
        </div>

        <nav>
          <ul className="drawer__list">
            {t.nav.map((item) => (
              <li key={item.href}>
                <a className="drawer__link" href={item.href} onClick={() => setOpen(false)}>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <Button
          icon="arrow"
          size="lg"
          className="drawer__cta"
          onClick={() => {
            setOpen(false);
            openLead();
          }}
        >
          {t.ctaShort}
        </Button>
      </aside>
    </>
  );
}

/* ------------------------------------------------------------------- hero -- */

function Waveform({ bars = 22, aura = false }: { bars?: number; aura?: boolean }) {
  return (
    <div className={`waveform ${aura ? "waveform--aura" : ""}`} aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => (
        <span key={i} style={{ animationDelay: `${(i % 7) * 120}ms` }} />
      ))}
    </div>
  );
}

function HeroVisual() {
  const { t } = useT();
  return (
    <div className="hero__visual">
      <div className="hero__portrait">
        <Media
          src={img.heroPortrait}
          srcSet={img.heroPortraitSet}
          sizes="(max-width: 767px) calc(60vw - 26px), (max-width: 1023px) 58vw, 32vw"
          label={t.hero.portrait}
          ratio="3 / 4"
          className="hero__portrait-img"
          priority
        />

        {/* เส้นสแกนใบหน้า — ไล่ขึ้นลง 3 รอบ หยุด 5 วินาที แล้วเริ่มใหม่ */}
        <span className="face-scan" aria-hidden="true">
          <span className="face-scan__frame" />
          <span className="face-scan__line" />
        </span>
      </div>

      <div className="hero__people">
        {t.hero.people.map((p, i) => (
          <Media
            key={p}
            src={img.people[i]}
            srcSet={img.peopleSet[i]}
            sizes="96px"
            label={p}
            ratio="1 / 1"
            className="hero__person"
          />
        ))}
      </div>

      <div className="hero__devices">
        <div className="device device--phone">
          <span className="voice-aura" aria-hidden="true" />
          <Waveform bars={16} aura />
          <p className="device--phone__text">
            {t.hero.phone.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </p>
          <span className="device--phone__mic" aria-hidden="true">
            <span className="mic-ring" />
            <span className="mic-ring mic-ring--delay" />
            <Icon name="microphone" size={22} />
          </span>
        </div>
      </div>

      {/* ผลตอบกลับสด ๆ ระหว่างฝึก — กระพริบต่อเนื่องเพื่อบอกว่าระบบยังทำงานอยู่ */}
      {t.hero.tips.map((tip, i) => (
        <span key={tip} className={`hero-tip hero-tip--${i + 1}`}>
          <span className="hero-tip__dot" aria-hidden="true" />
          {tip}
        </span>
      ))}

      <div className="float-card float-card--radar">
        <RadarChart axes={t.hero.radar.axes} label={t.hero.radar.title} />
      </div>

      <div className="float-card float-card--levels">
        <p className="float-card__title">{t.hero.levels.title}</p>
        <LevelBars bars={t.hero.levels.bars} label={t.hero.levels.title} />
        <p className="float-card__score">
          <CountUpValue text={t.hero.levels.score} />
          <span className="float-card__unit">{t.hero.levels.unit}</span>
        </p>
      </div>

      <div className="float-card float-card--practice">
        <Waveform bars={9} aura />
        <p>
          {t.hero.floatCard[0]}
          <br />
          {t.hero.floatCard[1]}
        </p>
      </div>

      <p className="script script--hero" aria-hidden="true">
        {t.hero.script}
      </p>
    </div>
  );
}

function Hero() {
  const { t } = useT();
  const { openLead } = useLead();
  // พิมพ์ทีละบรรทัด บรรทัดถัดไปเริ่มเมื่อบรรทัดก่อนหน้าจบ
  const [step, setStep] = useState(0);
  return (
    <section className="hero" id="top">
      <div className="hero__glow" aria-hidden="true" />
      <div className="hero__ribbon" aria-hidden="true" />
      <div className="container hero__inner">
        <div className="hero__content">
          <p className="eyebrow">{t.hero.eyebrow}</p>

          <h1
            className="hero__headline"
            aria-label={`${t.hero.headline[0]} ${t.hero.headline[1]}`}
          >
            <span>
              <Typing text={t.hero.headline[0]} run={step >= 0} onDone={() => setStep(1)} />
            </span>
            <span className="hero__headline-accent">
              <Typing text={t.hero.headline[1]} run={step >= 1} onDone={() => setStep(2)} />
            </span>
          </h1>

          <p className="hero__support">
            <Typing text={t.hero.support} run={step >= 2} speed={38} onDone={() => setStep(3)} />
          </p>
          <p className={`hero__english ${step >= 3 ? "is-shown" : ""}`}>{t.hero.english}</p>

          <ul className="benefits">
            {t.hero.benefits.map((b) => (
              <li key={b.icon} className="benefit">
                <span className="benefit__icon">
                  <Icon name={b.icon} size={26} />
                </span>
                <span className="benefit__text">
                  {b.line1}
                  <br />
                  {b.line2}
                </span>
              </li>
            ))}
          </ul>

          <div className="hero__actions">
            <Button size="lg" icon="arrow" className="btn--glow" onClick={openLead}>
              {t.hero.primaryCta}
            </Button>
          </div>
        </div>

        <div className="hero__media">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- industry -- */

/* ── Modal คัดกรองลูกค้า ────────────────────────────────────────────────────
   ถามสามขั้น: บทบาท → บริบทธุรกิจ → ข้อมูลติดต่อ
   แบ่งเป็นขั้นเพราะฟอร์มยาวหน้าเดียวทำให้คนกดออกกลางทาง และการรู้บทบาทก่อน
   ทำให้ทีมขายเตรียมตัวอย่างได้ตรงกว่าการได้อีเมลเปล่า ๆ

   ส่งผ่าน Netlify Forms จึงไม่ต้องมี backend
   ต้องมีฟอร์มซ่อนใน public/index.html ที่ชื่อฟิลด์ตรงกันด้วย                */

type LeadState = {
  role: string;
  industry: string[];
  business: string;
  size: string;
  timeline: string;
  name: string;
  org: string;
  position: string;
  email: string;
  phone: string;
};

const EMPTY_LEAD: LeadState = {
  role: '',
  industry: [],
  business: '',
  size: '',
  timeline: '',
  name: '',
  org: '',
  position: '',
  email: '',
  phone: '',
};

function LeadModal({
  open,
  onClose,
  presetRole,
}: {
  open: boolean;
  onClose: () => void;
  /** บทบาทที่ผู้ใช้เลือกมาแล้วจากการ์ดพันธมิตร ถ้ามีจะข้ามขั้นแรกไปเลย */
  presetRole?: string;
}) {
  const { t } = useT();
  const L = t.lead;
  const [step, setStep] = useState(0);
  const [data, setData] = useState<LeadState>(EMPTY_LEAD);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done'>('idle');
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  const set = <K extends keyof LeadState>(k: K, v: LeadState[K]) => {
    setData((d) => ({ ...d, [k]: v }));
    setError('');
  };

  const toggleIndustry = (v: string) => {
    setData((d) => ({
      ...d,
      industry: d.industry.includes(v)
        ? d.industry.filter((x) => x !== v)
        : [...d.industry, v],
    }));
    setError('');
  };

  // ล็อกการเลื่อนพื้นหลัง จำ element ที่กดเปิด แล้วคืนโฟกัสตอนปิด
  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    document.body.classList.add('is-locked');

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    const id = window.setTimeout(() => {
      panelRef.current
        ?.querySelector<HTMLElement>('[data-autofocus]')
        ?.focus();
    }, 60);

    return () => {
      document.body.classList.remove('is-locked');
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(id);
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose]);

  // เปิดใหม่ทุกครั้งให้เริ่มสถานะใหม่ ถ้ามาจากการ์ดที่ระบุบทบาทไว้แล้ว
  // ให้เลือกไว้ให้และข้ามไปขั้นสองทันที จะได้ไม่ต้องกดซ้ำสิ่งที่เพิ่งกดมา
  useEffect(() => {
    if (!open) return;
    setError('');
    setStatus('idle');
    if (presetRole) {
      setData((d) => ({ ...d, role: presetRole }));
      setStep(1);
    } else {
      setStep(0);
    }
  }, [open, presetRole]);

  if (!open) return null;

  const goNext = () => {
    if (step === 0 && !data.role) {
      setError(L.errorRole);
      return;
    }
    if (step === 1) {
      if (!data.industry.length) {
        setError(L.errorIndustry);
        return;
      }
      if (!data.size) {
        setError(L.errorSize);
        return;
      }
    }
    setError('');
    setStep((v) => Math.min(2, v + 1));
  };

  /* รวมคำตอบทั้งหมดเป็นข้อความอีเมล ใช้เป็นทางสำรองเมื่อส่งผ่านเว็บไม่ได้
     จะได้ไม่เสียลูกค้าไปเพราะปัญหาทางเทคนิคของฝั่งเรา                     */
  const mailHref = () => {
    const lines = [
      `${L.steps[0].title}: ${data.role}`,
      `${L.industryLabel}: ${data.industry.join(', ')}`,
      `${L.businessLabel}: ${data.business}`,
      `${L.sizeLabel}: ${data.size}`,
      `${L.timelineLabel}: ${data.timeline}`,
      '',
      `${L.nameLabel}: ${data.name}`,
      `${L.orgLabel}: ${data.org}`,
      `${L.positionLabel}: ${data.position}`,
      `${L.emailLabel}: ${data.email}`,
      `${L.phoneLabel}: ${data.phone}`,
    ].join('\n');
    return `mailto:info@talkblox.ai?subject=${encodeURIComponent(
      `${L.mailSubject} — ${data.org || data.name}`
    )}&body=${encodeURIComponent(lines)}`;
  };

  const submit = async () => {
    if (!data.name.trim() || !data.org.trim() || !data.email.trim()) {
      setError(L.errorContact);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      setError(L.errorEmail);
      return;
    }
    // Netlify Forms ทำงานเฉพาะบนโดเมนที่ deploy แล้วเท่านั้น
    // บน localhost หรือ sandbox การ POST จะไม่มีตัวรับ จึงข้ามไปทางอีเมลเลย
    const host = window.location.hostname;
    if (/localhost|127\.0\.0\.1|stackblitz|webcontainer|^$/i.test(host)) {
      setError(L.errorLocal);
      return;
    }

    setStatus('sending');
    const body = new URLSearchParams({
      'form-name': 'talkblox-lead',
      'bot-field': '',
      role: data.role,
      industry: data.industry.join(', '),
      business: data.business,
      size: data.size,
      timeline: data.timeline,
      name: data.name,
      org: data.org,
      position: data.position,
      email: data.email,
      phone: data.phone,
    });

    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('done');
    } catch (e) {
      setStatus('idle');
      const code = e instanceof Error ? e.message : 'network';
      setError(`${L.errorSend} (${code})`);
    }
  };

  const pct = status === 'done' ? 100 : ((step + 1) / 3) * 100;

  return (
    <div className="lead" role="presentation" onMouseDown={onClose}>
      <div className="lead__veil" />

      <div
        ref={panelRef}
        className="lead__panel"
        role="dialog"
        aria-modal="true"
        aria-label={L.title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* ไอสีเคลื่อนอยู่หลังกระจก ให้แผงดูมีชีวิตแทนที่จะเป็นกล่องขาวนิ่ง ๆ */}
        <span className="lead__aura lead__aura--1" aria-hidden="true" />
        <span className="lead__aura lead__aura--2" aria-hidden="true" />
        <span className="lead__aura lead__aura--3" aria-hidden="true" />

        <button
          type="button"
          className="lead__close"
          aria-label={L.close}
          onClick={onClose}
        >
          <Icon name="close" size={20} />
        </button>

        {status === 'done' ? (
          <div className="lead__done">
            <span className="lead__done-mark" aria-hidden="true">
              <span className="lead__done-ring" />
              <Icon name="analytics" size={26} />
            </span>
            <h2 className="lead__title">{L.doneTitle}</h2>
            <p className="lead__sub">{L.doneBody}</p>

            <ol className="lead-next">
              {L.doneSteps.map((st) => (
                <li key={st.n} className="lead-next__item">
                  <span className="lead-next__n">{st.n}</span>
                  <span className="lead-next__text">
                    <b>{st.label}</b>
                    {st.body}
                  </span>
                </li>
              ))}
            </ol>

            <p className="lead__contact">{L.doneContact}</p>

            <Button size="lg" icon="arrow" onClick={onClose}>
              {L.doneClose}
            </Button>
          </div>
        ) : (
          <>
            <header className="lead__head">
              <p className="lead__eyebrow">
                {L.stepLabel} {step + 1} {L.of} 3
              </p>
              <h2 className="lead__title">{L.title}</h2>
              <p className="lead__sub">{L.sub}</p>
              <div className="lead__bar">
                <span style={{ width: `${pct}%` }} />
              </div>
            </header>

            <div className="lead__body">
              <p className="lead__step-title">{L.steps[step].title}</p>
              <p className="lead__step-hint">{L.steps[step].hint}</p>

              {step === 0 && (
                <div className="lead__roles">
                  {L.roles.map((r, i) => (
                    <button
                      key={r.value}
                      type="button"
                      className={`lead-role ${
                        data.role === r.value ? 'is-on' : ''
                      }`}
                      onClick={() => set('role', r.value)}
                      {...(i === 0 ? { 'data-autofocus': 'true' } : {})}
                    >
                      <span className="lead-role__icon">
                        <Icon name={r.icon} size={20} />
                      </span>
                      <span className="lead-role__text">
                        <span className="lead-role__th">{r.value}</span>
                        <span className="lead-role__en">{r.en}</span>
                      </span>
                      <span className="lead-role__tick" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <div className="lead__fields">
                  <div className="lead-field">
                    <span className="lead-field__label">{L.industryLabel}</span>
                    <div className="lead__chips">
                      {L.industries.map((v, i) => (
                        <button
                          key={v}
                          type="button"
                          className={`lead-chip ${
                            data.industry.includes(v) ? 'is-on' : ''
                          }`}
                          onClick={() => toggleIndustry(v)}
                          {...(i === 0 ? { 'data-autofocus': 'true' } : {})}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="lead-field">
                    <span className="lead-field__label">{L.businessLabel}</span>
                    <input
                      className="lead-input"
                      value={data.business}
                      placeholder={L.businessPlaceholder}
                      onChange={(e) => set('business', e.target.value)}
                    />
                  </label>

                  {/* วางคู่กันเพื่อประหยัดความสูง แผงจะได้จบในหน้าจอเดียว */}
                  <div className="lead__pair">
                    <div className="lead-field">
                      <span className="lead-field__label">{L.sizeLabel}</span>
                      <div className="lead__chips">
                        {L.sizes.map((v) => (
                          <button
                            key={v}
                            type="button"
                            className={`lead-chip ${
                              data.size === v ? 'is-on' : ''
                            }`}
                            onClick={() => set('size', v)}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="lead-field">
                      <span className="lead-field__label">{L.timelineLabel}</span>
                      <div className="lead__chips">
                        {L.timelines.map((v) => (
                          <button
                            key={v}
                            type="button"
                            className={`lead-chip ${
                              data.timeline === v ? 'is-on' : ''
                            }`}
                            onClick={() => set('timeline', v)}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="lead__fields">
                  <div className="lead__grid">
                    <label className="lead-field">
                      <span className="lead-field__label">
                        {L.nameLabel} <i>{L.required}</i>
                      </span>
                      <input
                        className="lead-input"
                        value={data.name}
                        onChange={(e) => set('name', e.target.value)}
                        data-autofocus="true"
                      />
                    </label>

                    <label className="lead-field">
                      <span className="lead-field__label">
                        {L.orgLabel} <i>{L.required}</i>
                      </span>
                      <input
                        className="lead-input"
                        value={data.org}
                        onChange={(e) => set('org', e.target.value)}
                      />
                    </label>

                    <label className="lead-field">
                      <span className="lead-field__label">
                        {L.positionLabel}
                      </span>
                      <input
                        className="lead-input"
                        value={data.position}
                        onChange={(e) => set('position', e.target.value)}
                      />
                    </label>

                    <label className="lead-field">
                      <span className="lead-field__label">
                        {L.emailLabel} <i>{L.required}</i>
                      </span>
                      <input
                        className="lead-input"
                        type="email"
                        inputMode="email"
                        value={data.email}
                        onChange={(e) => set('email', e.target.value)}
                      />
                    </label>

                    <label className="lead-field lead-field--wide">
                      <span className="lead-field__label">{L.phoneLabel}</span>
                      <input
                        className="lead-input"
                        type="tel"
                        inputMode="tel"
                        value={data.phone}
                        onChange={(e) => set('phone', e.target.value)}
                      />
                    </label>
                  </div>

                </div>
              )}

              {error && (
                <div className="lead__error" role="alert">
                  <p>{error}</p>
                  {(error.startsWith(L.errorSend) || error === L.errorLocal) && (
                    <a className="lead__mail" href={mailHref()}>
                      <Icon name="mail" size={16} />
                      {L.mailFallback}
                    </a>
                  )}
                </div>
              )}
            </div>

            <footer className="lead__foot">
              <button
                type="button"
                className="lead__back"
                onClick={() => setStep((v) => Math.max(0, v - 1))}
                disabled={step === 0}
              >
                {L.back}
              </button>

              {step < 2 ? (
                <Button icon="arrow" onClick={goNext}>
                  {L.next}
                </Button>
              ) : (
                <Button icon="arrow" onClick={submit}>
                  {status === 'sending' ? L.sending : L.submit}
                </Button>
              )}
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

function IndustryCard({ item, delay }: { item: Copy["industry"]["items"][number]; delay: number }) {
  const { t } = useT();
  return (
    <Reveal as="article" className="industry-card" delay={delay}>
      <div className="industry-card__skew">
        <Media
          src={img.industry[item.icon]}
          srcSet={img.industrySet[item.icon]}
          sizes="(max-width: 1023px) 236px, 250px"
          label={`${t.industry.mediaPrefix} ${item.en}`}
          ratio="16 / 10"
          rounded={false}
          className="industry-card__img"
        />
        <div className="industry-card__overlay" aria-hidden="true" />
        <div className="industry-card__body">
          <span className="industry-card__icon">
            <Icon name={item.icon} size={22} />
          </span>
          <div>
            <h3 className="industry-card__title">
              {item.th[0]}
              <br />
              {item.th[1]}
            </h3>
            <p className="industry-card__en">{item.en}</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function IndustrySection() {
  const { t } = useT();
  const { openLead } = useLead();
  return (
    <section className="industry" id="industry" aria-label={t.industry.label}>
      <div className="container">
        <Reveal className="industry__head">
          <SectionHeading lines={t.industry.heading} accentLine={1} />
          <p className="industry__lead">{t.industry.lead}</p>
        </Reveal>

        <div className="industry__inner">
        <div className="industry__scroller">
          {t.industry.items.map((item, i) => (
            <IndustryCard key={item.en} item={item} delay={i * 100} />
          ))}
        </div>
          <Reveal className="industry__note" delay={220}>
            <p className="script script--industry">{t.industry.script}</p>
            <p className="industry__note-sub">{t.industry.scriptSub}</p>
            <span className="industry__note-arrow" aria-hidden="true">
              <Icon name="arrow" size={26} />
            </span>

            <Button className="btn--glow industry__note-cta" icon="arrow" onClick={openLead}>
              {t.industry.cta}
            </Button>
          </Reveal>
        </div>
      </div>

    </section>
  );
}

/* -------------------------------------------------------------------- why -- */

function FeatureCard({
  feature,
  index,
  delay,
}: {
  feature: Copy["why"]["features"][number];
  index: number;
  delay: number;
}) {
  return (
    <Reveal as="article" className="feature-card" delay={delay}>
      <div className="feature-card__text">
        <h3 className="feature-card__title">{feature.title}</h3>
        <p className="feature-card__body">{feature.body}</p>
      </div>
      <Media
        src={img.features[index]}
        srcSet={img.featureSet[index]}
        sizes="(max-width: 767px) calc(100vw - 48px), (max-width: 1023px) calc(100vw - 96px), 270px"
        label={feature.media}
        ratio="16 / 10"
        className="feature-card__media"
      />
    </Reveal>
  );
}

function WhySection() {
  const { t } = useT();
  return (
    <section className="why" id="why">
      <div className="container why__inner">
        {/* ข้อความกับกราฟอยู่ในแผงเดียวกัน ข้อความอยู่ครึ่งบน กราฟกินครึ่งล่าง
            เต็มความกว้าง คอลัมน์ซ้ายจึงสูงเท่าการ์ดฝั่งขวา ไม่เหลือช่องว่าง */}
        <Reveal className="why__intro">
          <div className="why__panel">
            <span className="why__panel-aura" aria-hidden="true" />
            <span className="why__panel-aura why__panel-aura--2" aria-hidden="true" />

            <div className="why__panel-text">
              <h2 className="why__title">
                <span>{t.why.title[0]}</span>
                <span className="why__title-brand">{t.why.title[1]}</span>
              </h2>
              <p className="why__body">{t.why.body}</p>

              <div className="why__panel-row">
                <Button href="#product" variant="outline">
                  {t.why.cta}
                </Button>
              </div>
            </div>

            <div className="why__chart">
              <GrowthChart
                label={t.why.features[2].media}
                weeks={t.why.chart.weeks}
                badge={t.why.chart.badge}
              />
            </div>
          </div>
        </Reveal>

        <div className="why__features">
          {t.why.features.slice(0, 2).map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} delay={i * 100} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- product -- */
/* ลำดับ SkillLab → SpeakUp → OnStage → Progress เป็นลำดับการใช้งานจริง
   จึงใช้ตัวเลขกำกับขั้นตอนได้อย่างมีเหตุผล ไม่ใช่การตกแต่ง */

function ProductSection() {
  const { t, lang } = useT();
  // ข้อความใน tips เริ่มพิมพ์เมื่อภาพเข้าสายตา ไม่ใช่ตอนโหลดหน้า
  const [shotRef, shotIn] = useInView<HTMLDivElement>(0.25);
  return (
    <section className="product" id="product">
      <div className="container product__inner">
        {/* แผนภาพสถาปัตยกรรมวางก่อนหัวข้อ ทำหน้าที่เปิดเรื่องว่าระบบรับอะไร
            เข้าไปบ้าง ก่อนจะไล่รายละเอียดสี่โมดูลด้านล่าง */}
        <Reveal className="product__diagram">
          <div className="diagram">
            <DiagramFx />

            <Media
              src={
                lang === 'en' ? img.productArchitectureEn : img.productArchitecture
              }
              srcSet={
                lang === 'en' ? img.productArchitectureEnSet : img.productArchitectureSet
              }
              sizes="(max-width: 767px) 100vw, (max-width: 1439px) calc(100vw - 96px), 1320px"
              label={t.product.diagram}
              ratio="1728 / 991"
              className="product__diagram-img"
            />

            {/* วงแหวนรอบสมอง AI ตรงกลางภาพ ตำแหน่งอิงเป็น % ของกรอบ */}
            <span className="ai-ring" aria-hidden="true">
              <span className="ai-ring__halo" />
              <span className="ai-ring__circle ai-ring__circle--1" />
              <span className="ai-ring__circle ai-ring__circle--2" />
              <span className="ai-ring__circle ai-ring__circle--3" />
              <span className="ai-ring__orbit">
                <i />
              </span>
              <span className="ai-ring__orbit ai-ring__orbit--rev">
                <i />
              </span>
            </span>

            {t.product.diagramStats.map((st, i) => (
              <span key={st.label} className={`dg-stat dg-stat--${i + 1}`}>
                <span className="dg-stat__value">
                  <CountUpValue text={st.value} />
                </span>
                <span className="dg-stat__label">{st.label}</span>
              </span>
            ))}

            {t.product.diagramTips.map((tip, i) => (
              <span key={tip} className={`dg-tip dg-tip--${i + 1}`}>
                <span className="dg-tip__dot" />
                {tip}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal className="product__intro">
          <SectionHeading lines={t.product.heading} accentLine={1} />
          <p className="product__body">{t.product.body}</p>
        </Reveal>

        <ol className="steps">
          {t.product.steps.map((s, i) => (
            <Reveal as="li" key={s.name} className="step" delay={i * 90}>
              <span className="step__aura" aria-hidden="true" />
              <span className="step__ghost" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="step__icon">
                <Icon name={s.icon} size={22} />
              </span>
              <p className="step__name">{s.name}</p>
              <h3 className="step__title">{s.title}</h3>
              <p className="step__body">{s.body}</p>
              <span className="step__edge" aria-hidden="true" />
            </Reveal>
          ))}
        </ol>

        <Reveal className="product__preview" delay={160}>
          <div className="shot" ref={shotRef}>
            <Media
              src={img.productDashboard}
              srcSet={img.productDashboardSet}
              sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 1439px) calc(100vw - 96px), 1320px"
              label={t.product.preview}
              ratio="1661 / 870"
              className="product__shot"
            />

            {/* AI กำลังตอบ — ฟองข้อความลอยใกล้ปากของตัวละคร */}
            {t.product.overlay.tips.map((tip, i) => (
              <span key={tip} className={`shot-tip shot-tip--${i + 1}`}>
                <span className="shot-tip__dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                {/* พิมพ์จบแล้วพัก 5 วินาที จากนั้นลบทิ้งแล้วพิมพ์ใหม่ วนไปเรื่อย ๆ */}
                <Typing text={tip} run={shotIn} speed={62} loop pause={5000} />
              </span>
            ))}

            {/* ป้ายคะแนนพัฒนาการ ลอยรอบภาพและกระพริบเป็นจังหวะช้า ๆ */}
            {t.product.overlay.scores.map((sc, i) => (
              <span key={sc.label} className={`shot-score shot-score--${i + 1}`}>
                <span className="shot-score__value">
                  <CountUpValue text={sc.value} />
                </span>
                <span className="shot-score__label">{sc.label}</span>
              </span>
            ))}

            {/* ไฟกะพริบตรงปุ่มไมค์ในภาพ บอกว่ากำลังรับเสียงอยู่ */}
            <span className="shot-mic" aria-hidden="true">
              <span className="shot-mic__ring" />
              <span className="shot-mic__ring shot-mic__ring--delay" />
              <span className="shot-mic__core" />
            </span>
            <span className="shot-mic-label">
              <span className="shot-mic-label__dot" aria-hidden="true" />
              {t.product.overlay.mic}
            </span>

            {/* ผลตอบกลับที่ระบบสรุปให้หลังฝึกจบ */}
            <span className="shot-feedback">
              <span className="shot-feedback__label">
                {t.product.overlay.feedbackLabel}
              </span>
              <span className="shot-feedback__value">
                <CountUpValue text={t.product.overlay.feedbackValue} />
              </span>
            </span>
          </div>

          <p className="product__note">{t.product.note}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- impact -- */

function ImpactSection() {
  const { t } = useT();
  return (
    <section className="impact" id="impact">
      <Media label={t.impact.bg} ratio="21 / 9" rounded={false} className="impact__bg" />
      <div className="impact__scrim" aria-hidden="true" />
      <div className="container impact__inner">
        <Reveal className="impact__intro">
          <h2 className="impact__title">
            <span>{t.impact.title[0]}</span>
            <span>{t.impact.title[1]}</span>
          </h2>
          <p className="impact__sub">{t.impact.sub}</p>
        </Reveal>

        <dl className="stats">
          {t.impact.stats.map((s, i) => (
            <Reveal as="div" key={s.value} className="stat" delay={i * 110}>
              <dt className="stat__value">
                <CountUpValue text={s.value} />
              </dt>
              <dd className="stat__label">
                {s.line1}
                <br />
                {s.line2}
              </dd>
            </Reveal>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- partner -- */

function WhiteLabelMockup() {
  const { t } = useT();
  return (
    <div className="whitelabel">
      <span className="whitelabel__badge">
        {t.partner.badge[0]}
        <br />
        {t.partner.badge[1]}
      </span>
      <div className="whitelabel__laptop">
        <div className="whitelabel__screen">
          <p className="whitelabel__logo">YOUR LOGO</p>
          <p className="whitelabel__powered">
            Powered by <Logo />
          </p>
        </div>
        <div className="whitelabel__base" aria-hidden="true" />
      </div>
    </div>
  );
}

function PartnerSection() {
  const { t } = useT();
  const { openLead } = useLead();
  return (
    <section className="partner" id="partner">
      <div className="container partner__inner">
        <Reveal className="partner__intro">
          <SectionHeading lines={t.partner.title} />
          <p className="partner__body">{t.partner.body}</p>
          <Button size="lg" icon="arrow" className="btn--glow" onClick={openLead}>
            {t.partner.cta}
          </Button>
        </Reveal>

        <div className="partner__types">
          {t.partner.types.map((p, i) => (
            <Reveal as="div" key={p.en} className="partner-slot" delay={i * 90}>
              {/* กดแล้วเปิดแบบประเมินโดยเลือกบทบาทนี้ไว้ให้เลย
                  ผู้ใช้จึงเริ่มที่ขั้นสองทันที ไม่ต้องเลือกซ้ำสิ่งที่เพิ่งกด */}
              <button
                type="button"
                className={`partner-card partner-card--${i + 1}`}
                onClick={() => openLead(p.role)}
              >
                <span className="partner-card__glow" aria-hidden="true" />
                <span className="partner-card__icon">
                  <Icon name={p.icon} size={26} />
                </span>
                <span className="partner-card__title">{p.th}</span>
                <span className="partner-card__en">{p.en}</span>
                <span className="partner-card__go" aria-hidden="true">
                  <Icon name="arrow" size={16} />
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="partner__mockup" delay={180}>
          <WhiteLabelMockup />
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- conversion -- */

function ConversionSection() {
  const { t } = useT();
  return (
    <section className="conversion" id="contact">
      <div className="conversion__wash" aria-hidden="true" />
      <div className="container conversion__inner">
        <Reveal className="conversion__lead">
          <h2 className="conversion__title">
            <span>{t.conversion.title[0]}</span>
            <span>{t.conversion.title[1]}</span>
          </h2>
          <p className="conversion__sub">{t.conversion.sub}</p>
        </Reveal>

        <Reveal className="conversion__cta" delay={80}>
          {/* กดแล้วต่อสายทันที ไม่ต้องผ่านหน้าฟอร์ม */}
          <Button href="tel:+66952893646" size="lg" icon="arrow">
            {t.conversion.cta}
          </Button>
        </Reveal>

        <Reveal as="ul" className="contacts" delay={140}>
          {contacts.map((c) => (
            <li key={c.icon} className="contact">
              <span className="contact__icon">
                <Icon name={c.icon} size={20} />
              </span>
              {c.href ? (
                <a className="contact__text" href={c.href}>
                  {c.text}
                </a>
              ) : (
                <span className="contact__text">{c.text}</span>
              )}
            </li>
          ))}
        </Reveal>

        <p className="script script--conversion" aria-hidden="true">
          {t.conversion.script}
        </p>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- footer -- */

function Footer() {
  const { t } = useT();
  return (
    <footer className="footer">
      <div className="container footer__inner">
        <a className="footer__logo" href="#top" aria-label={t.home}>
          <Logo />
        </a>

        <nav className="footer__nav" aria-label={t.footer.navLabel}>
          <ul>
            {t.nav.map((item) => (
              <li key={item.href}>
                <a href={item.href}>{item.label}</a>
              </li>
            ))}
          </ul>
        </nav>

        <ul className="socials">
          {socials.map((s) => (
            <li key={s.icon}>
              <a href={s.href} aria-label={s.label} className="social">
                <SocialIcon name={s.icon} />
              </a>
            </li>
          ))}
        </ul>

        <p className="footer__legal">
          {t.footer.copyright[0]}
          <br />
          {t.footer.copyright[1]}
        </p>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------- app -- */

export default function App({ defaultLang = "th" as Lang }) {
  const [lang, setLang] = useState<Lang>(defaultLang);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadRole, setLeadRole] = useState<string | undefined>(undefined);
  const t = copy[lang] as unknown as Copy;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    // CSS อ่านค่านี้เพื่อรู้ว่าควรปิดการเคลื่อนไหวแบบวนไม่จบตามค่าระบบหรือไม่
    document.documentElement.dataset.motion = RESPECT_REDUCED_MOTION_AMBIENT
      ? "system"
      : "always";
  }, []);

  const toggle = () => setLang((l) => (l === "th" ? "en" : "th"));

  return (
    <LangContext.Provider value={{ lang, t, toggle }}>
      <LeadContext.Provider
        value={{
          openLead: (role?: string) => {
            setLeadRole(role);
            setLeadOpen(true);
          },
        }}
      >
      <a className="skip-link" href="#main">
        {t.skip}
      </a>
      <Header />
      <main id="main">
        <Hero />
        <IndustrySection />
        <WhySection />
        <ProductSection />
        <ImpactSection />
        <PartnerSection />
        <ConversionSection />
      </main>
        <Footer />

        <LeadModal
          open={leadOpen}
          onClose={() => setLeadOpen(false)}
          presetRole={leadRole}
        />
      </LeadContext.Provider>
    </LangContext.Provider>
  );
}